import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  MessagesSquare, Plus, Send, X, Users, Search, CheckCircle2, Circle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  fetchThreads, createThread, fetchMessages, postMessage, markThreadRead,
  clearChatError, clearGoneThread,
} from '../../redux/chatThreadSlice';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS } from '../../components/common/ModalShell';
import baseURL from '../../utils/baseURL';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { formatTime } from '../../utils/clock';

// TEAM CHAT (Phase 4, #14) — staff <-> staff inside the hospital, on the
// chat-threads backbone. Who may VIEW/START is the sub-role's chat permissions;
// this screen is behind PermissionGuard('chat.team.view') in App.jsx and the
// API enforces both again.
//
// UNREAD + PREVIEW ARE THE SERVER'S (owner batch, 16 Sep 2026). Each row of
// GET /chat-threads carries `unreadCount` and `lastMessage`, and opening a
// thread — or a poll landing a newer message in the open one — POSTs
// /chat-threads/:id/read. The badge is therefore the same in this tab, in
// another browser and in the Pro-App, which a per-device cursor could never be.

const THREAD_POLL_MS = 30 * 1000;
const MESSAGE_POLL_MS = 6 * 1000;
// How close to the top (px) a scroll has to come before the older page loads.
const LOAD_OLDER_THRESHOLD_PX = 40;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return new Date(iso).toLocaleDateString();
};

const TeamChatPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    threads, threadsLoading, messagesByThread, messagesLoading, olderLoading, noOlderByThread,
    sending, error, goneThreadId,
  } = useSelector((s) => s.chatThreads);

  const myId = localStorage.getItem('ID') || '';
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState('');
  const [showNew, setShowNew] = useState(false);
  // Colleague directory for the picker AND for naming message senders.
  const [colleagues, setColleagues] = useState([]);
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  // The poll's cursor. Read through a REF, not the render's `messages`: the
  // interval is created once per thread, so a value closed over at that moment
  // would stay frozen at whatever the thread held when it was opened (P32).
  const lastIdRef = useRef(null);
  // Distance from the bottom before an older page lands, so the reader's place
  // is kept instead of jumping when rows are added above them.
  const olderAnchorRef = useRef(null);
  // The newest id already reported as read for the OPEN thread, so a poll that
  // brings nothing new does not re-post the same marker every six seconds.
  const markedRef = useRef(null);
  // Set once the reader has been at the bottom of the open thread. Opening a
  // thread scrolls smoothly DOWN from the top; without this the first scroll
  // events of that animation would read as "reached the top" and pull in
  // every older page at once.
  const settledRef = useRef(false);

  const messages = messagesByThread[activeId] || [];
  const activeThread = threads.find((th) => th.threadId === activeId) || null;
  const firstId = messages.length ? messages[0].id : null;
  const lastId = messages.length ? messages[messages.length - 1].id : null;
  const noOlder = Boolean(activeId && noOlderByThread?.[activeId]);

  useEffect(() => { lastIdRef.current = lastId; }, [lastId]);

  useEffect(() => {
    dispatch(fetchThreads());
    const id = setInterval(() => dispatch(fetchThreads()), THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [dispatch]);

  // The open conversation polls with an `after` cursor — cheap, and the only
  // sanctioned live channel (message content never rides the socket). The
  // cursor advances with every message that lands (lastIdRef), and whatever
  // comes back is APPENDED by the slice, never swapped in for the list.
  useEffect(() => {
    if (!activeId) return undefined;
    markedRef.current = null;
    settledRef.current = false;
    dispatch(fetchMessages({ threadId: activeId }));
    const id = setInterval(() => {
      dispatch(fetchMessages({ threadId: activeId, after: lastIdRef.current }));
    }, MESSAGE_POLL_MS);
    return () => clearInterval(id);
  }, [activeId, dispatch]);

  // Follow the NEWEST message only — an older page arriving at the top must
  // not drag the reader down to the bottom.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lastId]);

  // HAVING THE THREAD OPEN IS READING IT. The marker follows the newest message
  // on screen — on open and on every poll that brings one — so the badge is
  // already clear here, in another tab and on the phone.
  useEffect(() => {
    if (!activeId || lastId == null) return;
    if (markedRef.current != null && Number(lastId) <= Number(markedRef.current)) return;
    markedRef.current = lastId;
    dispatch(markThreadRead({ threadId: activeId, messageId: lastId }));
  }, [activeId, lastId, dispatch]);

  // After an older page lands, put the reader back where they were.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || olderAnchorRef.current == null) return;
    el.scrollTop = el.scrollHeight - olderAnchorRef.current;
    olderAnchorRef.current = null;
  }, [firstId]);
  // An older page that brought nothing (the top) leaves no anchor behind for
  // the next thread to trip over.
  useEffect(() => {
    if (!olderLoading) olderAnchorRef.current = null;
  }, [olderLoading]);

  // Scrolling up to the top loads the page before the oldest message shown.
  const loadOlder = () => {
    if (!activeId || firstId == null || olderLoading || noOlder || messagesLoading) return;
    const el = listRef.current;
    olderAnchorRef.current = el ? el.scrollHeight - el.scrollTop : null;
    dispatch(fetchMessages({ threadId: activeId, before: firstId }));
  };
  const onListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= LOAD_OLDER_THRESHOLD_PX) settledRef.current = true;
    if (settledRef.current && scrollTop <= LOAD_OLDER_THRESHOLD_PX) loadOlder();
  };

  useEffect(() => {
    if (error) {
      // 30 Sep 2026: a refusal's `message` can be { en, fr } — handed to the
      // toast as-is it is an object rendered as a React child. Always a string.
      toast.error(extractErrorMessage(error, t('teamChat.failed', 'Something went wrong. Please try again.'), (i18n.language || 'en').slice(0, 2)));
      dispatch(clearChatError());
    }
  }, [error, dispatch, t, i18n.language]);

  // A thread that 404'd (access revoked / feature off) deselects ONCE — the
  // poll would otherwise toast the same error every cycle.
  useEffect(() => {
    if (goneThreadId && goneThreadId === activeId) setActiveId(null);
    if (goneThreadId) dispatch(clearGoneThread());
  }, [goneThreadId, activeId, dispatch]);

  // Staff + doctors, once — the invite picker and the sender-name lookup.
  useEffect(() => {
    const axiosInstance = createAuthedAxios();
    (async () => {
      const found = [];
      try {
        const { data } = await axiosInstance.get(`${baseURL}/staff?limit=100`);
        for (const s of data.staff || []) {
          found.push({
            id: s.staffId, role: 'user',
            name: [s.name, s.surname].filter(Boolean).join(' ') || t('teamChat.staff', 'Staff'),
            sub: s.roleName || s.userRoleName || t('teamChat.staff', 'Staff'),
          });
        }
      } catch { /* staff list may be closed to this role */ }
      try {
        const { data } = await axiosInstance.get(`${baseURL}/doctors?page=1&limit=100`);
        for (const d of data.doctors || data.rows || []) {
          found.push({
            id: d.doctorId, role: 'doctor',
            name: [d.name, d.surname].filter(Boolean).join(' ') || t('teamChat.doctor', 'Doctor'),
            sub: d.specialty || t('teamChat.doctor', 'Doctor'),
          });
        }
      } catch { /* doctor list may be closed to this role */ }
      setColleagues(found);
    })();
  }, [t]);

  // No system id on screen (owner, 29 Sep 2026): someone missing from the
  // directory reads as "Doctor" / "Staff" / "Admin" (the role their thread seat
  // says), never as their staffId / doctorId.
  //
  // THE SERVER NAMES PEOPLE NOW (30 Sep 2026). The directory above is the staff
  // list, which only an admin may read — for everybody else every bubble said
  // "Staff". Each `participants[]` entry of GET /chat-threads carries `name`, and
  // every message (list and send answer) carries `senderName`; both win, the
  // directory is the fallback, and the role word is the last resort.
  const seatOf = useMemo(() => {
    const seats = new Map();
    for (const th of threads || []) {
      for (const p of th.participants || []) {
        const prev = seats.get(p.participantId);
        seats.set(p.participantId, {
          role: p.participantRole || prev?.role,
          name: (typeof p.name === 'string' && p.name.trim()) || prev?.name || null,
        });
      }
    }
    return seats;
  }, [threads]);
  const roleWord = useCallback((role) => {
    if (role === 'doctor') return t('teamChat.doctor', 'Doctor');
    if (role === 'admin') return t('teamChat.admin', 'Admin');
    return t('teamChat.staff', 'Staff');
  }, [t]);
  const nameOf = useCallback(
    (id, serverName, role) => (typeof serverName === 'string' && serverName.trim())
      || seatOf.get(id)?.name
      || colleagues.find((c) => c.id === id)?.name
      || roleWord(seatOf.get(id)?.role || role),
    [colleagues, seatOf, roleWord]
  );

  // The box is cleared only once the server HAS the message (P43). A refused
  // or failed send leaves the text where it was, to fix or retry — and text
  // typed while the send was in flight is never thrown away either.
  const send = async () => {
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    const res = await dispatch(postMessage({ threadId: activeId, body }));
    if (res.meta.requestStatus === 'fulfilled') {
      setDraft((current) => (current.trim() === body ? '' : current));
    }
  };

  const threadTitle = (th) => th.title
    || (th.participants || []).filter((p) => p.participantId !== myId).map((p) => nameOf(p.participantId, p.name, p.participantRole)).join(', ')
    || t('teamChat.untitled', 'Conversation');

  return (
    <div className="h-full" data-testid="team-chat-page">
      <div className="bg-white rounded-2xl shadow-md h-full flex overflow-hidden">
        {/* ---- thread list ---- */}
        <div className={`w-full md:w-80 border-r border-gray-100 flex flex-col ${activeId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center">
                <MessagesSquare size={18} className="text-white" />
              </div>
              <h1 className="text-base font-bold text-gray-800">{t('teamChat.title', 'Team chat')}</h1>
            </div>
            <button
              onClick={() => setShowNew(true)}
              className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20 transition-colors flex items-center justify-center cursor-pointer"
              data-testid="team-chat-new"
              title={t('teamChat.new', 'New conversation')}
            >
              <Plus size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {threadsLoading && !threads.length ? (
              <div className="p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse flex gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-100" />
                    <div className="flex-1 space-y-2 py-1">
                      <div className="h-3 bg-gray-100 rounded w-2/3" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center">
                <Users size={36} className="mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-500">{t('teamChat.empty', 'No conversations yet.')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('teamChat.empty_hint', 'Start one with a colleague.')}</p>
              </div>
            ) : (
              threads.map((th) => (
                <button
                  key={th.threadId}
                  onClick={() => setActiveId(th.threadId)}
                  className={`w-full text-left px-4 py-3 flex gap-3 items-center border-b border-gray-50 transition-colors cursor-pointer ${activeId === th.threadId ? 'bg-[#4ABDBD]/10' : 'hover:bg-gray-50'}`}
                  data-testid={`team-thread-${th.threadId}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#9DB3B1]/20 text-[#2D8A8A] flex items-center justify-center shrink-0 font-bold text-sm">
                    {threadTitle(th).slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-800 truncate">{threadTitle(th)}</p>
                    <p className="text-[11px] text-gray-400 truncate" data-testid={`team-thread-preview-${th.threadId}`}>
                      {th.lastMessage
                        ? `${String(th.lastMessage.senderId) === String(myId)
                          ? t('chat.you', 'You')
                          : nameOf(th.lastMessage.senderId, th.lastMessage.senderName, th.lastMessage.senderRole)}: ${th.lastMessage.body}`
                        : `${(th.participants || []).length} ${t('teamChat.members', 'members')}`}
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <span className="text-[11px] text-gray-400">{timeAgo(th.lastMessageAt)}</span>
                    {th.unreadCount > 0 && (
                      <span
                        className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#2D8A8A] text-white text-[10px] font-bold flex items-center justify-center"
                        data-testid={`team-thread-unread-${th.threadId}`}
                      >
                        {th.unreadCount > 99 ? '99+' : th.unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ---- conversation ---- */}
        <div className={`flex-1 flex-col ${activeId ? 'flex' : 'hidden md:flex'}`}>
          {!activeThread ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <MessagesSquare size={44} className="text-gray-200 mb-3" />
              <p className="text-sm text-gray-500">{t('teamChat.pick', 'Pick a conversation, or start a new one.')}</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{threadTitle(activeThread)}</p>
                  <p className="text-[11px] text-gray-400 truncate">
                    {(activeThread.participants || []).map((p) => nameOf(p.participantId, p.name, p.participantRole)).join(' · ')}
                  </p>
                </div>
                <button onClick={() => setActiveId(null)} className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div
                ref={listRef}
                onScroll={onListScroll}
                className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50/60"
                data-testid="team-chat-messages"
              >
                {olderLoading && (
                  <div className="flex items-center justify-center gap-2 py-1 text-[11px] text-gray-400" data-testid="team-chat-loading-older">
                    <span className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-3.5 h-3.5 animate-spin" />
                    {t('teamChat.loading_older', 'Loading earlier messages…')}
                  </div>
                )}
                {messagesLoading && !messages.length ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className={`animate-pulse flex ${i % 2 ? 'justify-end' : ''}`}>
                        <div className="h-10 bg-gray-100 rounded-2xl w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.senderId === myId;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${mine ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white' : 'bg-white text-gray-800'}`}>
                          {!mine && (
                            <p className="text-[11px] font-semibold text-[#2D8A8A] mb-0.5">{nameOf(m.senderId, m.senderName, m.senderRole)}</p>
                          )}
                          <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                          <p className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                            {m.created_at ? formatTime(m.created_at) : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <div className="p-3.5 border-t border-gray-100 flex items-center gap-2.5">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={t('teamChat.placeholder', 'Write a message…')}
                  maxLength={4000}
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm"
                  data-testid="team-chat-input"
                />
                <button
                  onClick={send}
                  disabled={sending || !draft.trim()}
                  className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white flex items-center justify-center shadow-md hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all disabled:opacity-50 cursor-pointer"
                  data-testid="team-chat-send"
                >
                  <Send size={17} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <NewThreadModal
          colleagues={colleagues.filter((c) => c.id !== myId)}
          onClose={() => setShowNew(false)}
          onCreate={async ({ title, picked }) => {
            const res = await dispatch(createThread({
              title,
              participants: picked.map((p) => ({ participantId: p.id, participantRole: p.role })),
            }));
            if (res.meta.requestStatus === 'fulfilled') {
              setShowNew(false);
              const tid = res.payload.thread?.threadId;
              if (tid) setActiveId(tid);
              dispatch(fetchThreads());
            }
          }}
        />
      )}
    </div>
  );
};

const NewThreadModal = ({ colleagues, onClose, onCreate }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? colleagues.filter((c) => c.name.toLowerCase().includes(q)) : colleagues;
  }, [colleagues, query]);

  const toggle = (c) => setPicked((prev) =>
    prev.some((p) => p.id === c.id) ? prev.filter((p) => p.id !== c.id) : [...prev, c]);

  return (
    <ModalShell
      title={t('teamChat.new', 'New conversation')}
      onClose={onClose}
      size="md"
      testId="team-chat-new-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} className={MODAL_CANCEL_CLASS}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={async () => { setBusy(true); try { await onCreate({ title: title.trim(), picked }); } finally { setBusy(false); } }}
            disabled={busy || picked.length === 0}
            className={MODAL_SUBMIT_CLASS}
            data-testid="team-chat-create"
          >
            {busy && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" />}
            {t('teamChat.start', 'Start conversation')}
          </button>
        </>
      )}
    >
        <div className="space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('teamChat.title_placeholder', 'Subject (optional) — e.g. Night shift handover')}
            maxLength={120}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm"
          />
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('teamChat.search_people', 'Find colleagues…')}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm"
            />
          </div>
          <div className="border border-gray-100 rounded-xl divide-y divide-gray-50 max-h-56 overflow-y-auto">
            {shown.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">{t('teamChat.nobody', 'Nobody to show')}</p>
            ) : shown.map((c) => {
              const on = picked.some((p) => p.id === c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c)}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  data-testid={`team-chat-pick-${c.id}`}
                >
                  {on
                    ? <CheckCircle2 size={18} className="text-[#4ABDBD] shrink-0" />
                    : <Circle size={18} className="text-gray-300 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                    <p className="text-[11px] text-gray-400 truncate">{c.sub}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
    </ModalShell>
  );
};

export default TeamChatPage;
